import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import SectionHeading from "@/components/base/SectionHeading";
import MovieCard from "@/components/base/MovieCard";
import ReviewCard from "@/components/base/ReviewCard";
import HeroCarousel from "./components/HeroCarousel";
import CategoryShortcuts from "./components/CategoryShortcuts";
import SiteIntro from "./components/SiteIntro";
import { getFeaturedMovies, getLatestMovies, getLatestReviews } from "@/utils/catalog";
import { movies } from "@/mocks/movies";
import { reviews } from "@/mocks/reviews";

const stats = [
  { value: "12", label: "收录影视作品", icon: "ri-film-line" },
  { value: "14", label: "影评与观后笔记", icon: "ri-quill-pen-line" },
  { value: "6", label: "题材分类", icon: "ri-price-tag-3-line" },
  { value: "128.4 万", label: "累计影迷浏览", icon: "ri-eye-line" },
];

export default function Home() {
  usePageMeta(
    "苍井空电影大全-最新影视作品资讯与影评",
    "苍井空电影网站提供丰富的苍井空影视作品资源，涵盖电影介绍、影评与相关资讯，满足影迷全面了解苍井空电影的需求。",
  );
  const featured = getFeaturedMovies(3);
  const latest = getLatestMovies(6);
  const latestReviews = getLatestReviews(3);

  return (
    <SiteLayout>
      <h1 className="sr-only">苍井空电影大全 - 最新影视作品资讯与影评</h1>
      <HeroCarousel movies={featured} />

      <div className="mx-auto -mt-10 w-full max-w-[1320px] px-4 md:px-8">
        <div className="relative z-10 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-background-200 bg-background-200 lg:grid-cols-4">
          {stats.map((stat) => (
            <div key={stat.label} className="flex items-center gap-3 bg-background-50 px-4 py-4 md:px-6 md:py-5">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                <i className={`${stat.icon} text-base leading-none`} />
              </span>
              <div className="min-w-0">
                <p className="font-heading text-lg leading-none text-foreground-950 md:text-xl">{stat.value}</p>
                <p className="mt-1 truncate text-[11px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="mx-auto w-full max-w-[1320px] px-4 pt-12 md:px-8 md:pt-14" aria-labelledby="latest-movies">
        <SectionHeading
          eyebrow="Recently updated"
          title="最新上线的作品"
          description="按资料更新时间排序，先看最近补全信息与影评的作品。"
          actionLabel="进入分类页"
          actionTo="/category"
        />
        <div id="latest-movies" className="grid gap-4 sm:hidden">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} variant="row" />
          ))}
        </div>
        <div className="hidden gap-x-4 gap-y-6 sm:grid sm:grid-cols-3 lg:grid-cols-6">
          {latest.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      </section>

      <section className="mt-12 w-full border-y border-background-200 bg-background-100 py-12 md:mt-14 md:py-14" aria-labelledby="genre-entry">
        <div className="mx-auto w-full max-w-[1320px] px-4 md:px-8">
          <SectionHeading
            eyebrow="Browse by genre"
            title="按题材快速进入"
            description="从剧情、爱情到纪录与动作，按你当下想看的方向筛选作品。"
          />
          <div id="genre-entry">
            <CategoryShortcuts />
          </div>
        </div>
      </section>

      <div className="mx-auto grid w-full max-w-[1320px] gap-8 px-4 pt-12 md:px-8 md:pt-14 lg:grid-cols-[1fr_320px]">
        <section aria-labelledby="featured-reviews">
          <SectionHeading
            eyebrow="Selected reviews"
            title="影评精选"
            description="来自影迷的长文与观后笔记，试着从另一个角度再看一遍。"
            actionLabel="全部影评"
            actionTo="/reviews"
          />
          <div id="featured-reviews" className="grid gap-4 md:grid-cols-3">
            {latestReviews.map((review) => (
              <ReviewCard key={review.id} review={review} />
            ))}
          </div>
        </section>

        <aside className="lg:pt-1" aria-label="站点速览">
          <div className="rounded-lg border border-background-200 bg-background-100/70 p-5">
            <h2 className="font-heading text-base text-foreground-950">编辑手记</h2>
            <p className="mt-2 text-xs leading-relaxed text-foreground-600">
              资料以作品为单位整理：基本信息公开可查，剧情简介不做剧透，
              影评部分保留作者个人观点。若发现信息有误，欢迎通过反馈表单告诉我们。
            </p>
            <div className="mt-4 space-y-2 border-t border-background-200 pt-4 text-[11px] text-foreground-500">
              <p className="flex items-center justify-between">
                <span>作品总数</span>
                <span className="text-foreground-800">{movies.length} 部</span>
              </p>
              <p className="flex items-center justify-between">
                <span>影评总数</span>
                <span className="text-foreground-800">{reviews.length} 篇</span>
              </p>
              <p className="flex items-center justify-between">
                <span>最近更新</span>
                <span className="text-foreground-800">2024 / 12 / 15</span>
              </p>
            </div>
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to="/category"
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 py-2.5 text-xs text-background-50 transition-colors hover:bg-primary-700"
              >
                <i className="ri-filter-3-line text-sm" />
                按条件筛选作品
              </Link>
              <Link
                to="/reviews"
                className="flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <i className="ri-book-open-line text-sm" />
                阅读全部影评
              </Link>
            </div>
          </div>

          <div className="mt-4 rounded-lg border border-accent-200 bg-accent-50 p-5">
            <p className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-accent-800">
              <i className="ri-lightbulb-line text-sm" />
              Viewing note
            </p>
            <p className="mt-2 font-heading text-sm text-foreground-900">观影小提示</p>
            <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
              《白色回声》与《慢速快门》为纪录作品，节奏偏慢，建议留出完整的时间观看。
            </p>
            <Link
              to="/detail/white-echo"
              className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700 hover:text-primary-800"
            >
              查看《白色回声》
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </aside>
      </div>

      <div className="mt-12 md:mt-16">
        <SiteIntro />
      </div>
    </SiteLayout>
  );
}